import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Card, CardBody, CardTitle, Spinner, FormGroup, Input, Button } from 'reactstrap';
import useAxios from 'axios-hooks';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
  PieChart,
  Pie,
  Cell,
} from 'recharts';
// import moment from 'moment';
import { saveAs } from 'file-saver';
import { stringify } from 'csv-stringify/browser/esm/sync';
import t from 'lib/translate';

const BAR_COLOR = '#08489b';
const PIE_COLORS = [
  '#08489b',
  '#1f77b4',
  '#ff7f0e',
  '#2ca02c',
  '#d62728',
  '#9467bd',
  '#8c564b',
  '#e377c2',
  '#7f7f7f',
  '#bcbd22',
  '#17becf',
  '#aec7e8',
];

const AccountingDocumentPercentageChart = ({ data, dataKey, color }) => {
  const formatDisplayName = (key) => {
    return t(key);
  };

  const formatTooltipValue = (value, name, entry) => {
    const dataPoint = entry.payload;
    const key = entry.dataKey;
    let editField;
    let total = dataPoint.documents_total;

    if (key === 'percentage_perfect') {
      // Special case for overall percentage
      editField = 'documents_with_edits';
    } else {
      // For field-specific percentages
      const baseField = key.replace('_perfect_pct', '');
      editField = `${baseField}_edits`;
    }

    const numPerfect = total - dataPoint[editField];
    return [`${value}% (${numPerfect} / ${total})`, t(key)];
  };

  return (
    <div style={{ width: '100%', height: 500, marginBottom: 30 }}>
      <ResponsiveContainer>
        <BarChart
          data={data}
          margin={{
            top: 20,
            right: 30,
            left: 20,
            bottom: 5,
          }}
        >
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis dataKey="month" />
          <YAxis domain={[0, 100]} label={{ value: 'Percentage (%)', angle: -90, position: 'insideLeft' }} />
          <Tooltip formatter={formatTooltipValue} />
          <Legend />
          <Bar dataKey={dataKey} fill={color} name={formatDisplayName(dataKey)} isAnimationActive={true} />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
};

const AverageEditsChart = ({ data, dataKey, color }) => {
  const formatDisplayName = (key) => {
    return t(key);
  };

  const formatTooltipValue = (value, name) => {
    return [`${parseFloat(value).toFixed(2)}`, t(name)];
  };

  return (
    <div style={{ width: '100%', height: 500, marginBottom: 30 }}>
      <ResponsiveContainer>
        <BarChart
          data={data}
          margin={{
            top: 20,
            right: 30,
            left: 20,
            bottom: 5,
          }}
        >
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis dataKey="month" />
          <YAxis label={{ value: 'Average Edits', angle: -90, position: 'insideLeft' }} />
          <Tooltip formatter={formatTooltipValue} />
          <Legend />
          <Bar dataKey={dataKey} fill={color} name={formatDisplayName(dataKey)} isAnimationActive={true} />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
};

// const EditTimeChart = ({ data, dataKey, color }) => {
//   const formatDisplayName = (key) => {
//     return t(key);
//   };

//   const formatTooltipValue = (seconds, name) => {
//     const duration = moment.duration(seconds, 'seconds');
//     const formattedDuration = moment.utc(duration.asMilliseconds()).format('HH:mm:ss');
//     return [formattedDuration, t(name)];
//   };

//   return (
//     <div style={{ width: '100%', height: 500, marginBottom: 30 }}>
//       <ResponsiveContainer>
//         <BarChart
//           data={data}
//           margin={{
//             top: 20,
//             right: 30,
//             left: 20,
//             bottom: 5,
//           }}
//         >
//           <CartesianGrid strokeDasharray="3 3" />
//           <XAxis dataKey="month" />
//           <YAxis label={{ value: 'Average Time', angle: -90, position: 'insideLeft' }} />
//           <Tooltip formatter={formatTooltipValue} />
//           <Legend />
//           <Bar dataKey={dataKey} fill={color} name={formatDisplayName(dataKey)} isAnimationActive={true} />
//         </BarChart>
//       </ResponsiveContainer>
//     </div>
//   );
// };

AccountingDocumentPercentageChart.propTypes = {
  data: PropTypes.array.isRequired,
  dataKey: PropTypes.string.isRequired,
  color: PropTypes.string.isRequired,
};

AverageEditsChart.propTypes = {
  data: PropTypes.array.isRequired,
  dataKey: PropTypes.string.isRequired,
  color: PropTypes.string.isRequired,
};

// EditTimeChart.propTypes = {
//   data: PropTypes.array.isRequired,
//   dataKey: PropTypes.string.isRequired,
//   color: PropTypes.string.isRequired,
// };

const EditDistributionPieChart = ({ data }) => {
  if (!data || data.length === 0) {
    return <div>No data available</div>;
  }

  const formatTooltipValue = (value, name) => {
    const total = data.reduce((sum, item) => sum + item.edit_count, 0);
    const percentage = ((value / total) * 100).toFixed(1);
    return [`${value} (${percentage}%)`, name];
  };

  const formatLabelValue = (entry) => {
    const total = data.reduce((sum, item) => sum + item.edit_count, 0);
    const percentage = ((entry.edit_count / total) * 100).toFixed(1);
    return `${percentage}%`;
  };

  const formatLegendValue = (value) => {
    return value;
  };

  return (
    <div style={{ width: '100%', height: 500, marginBottom: 30 }}>
      <ResponsiveContainer>
        <PieChart>
          <Pie
            data={data}
            cx="50%"
            cy="50%"
            labelLine={false}
            label={formatLabelValue}
            outerRadius={150}
            fill="#8884d8"
            dataKey="edit_count"
            nameKey="mutation"
          >
            {data.map((entry, index) => (
              <Cell key={`cell-${index}`} fill={PIE_COLORS[index % PIE_COLORS.length]} />
            ))}
          </Pie>
          <Tooltip formatter={formatTooltipValue} />
          <Legend formatter={formatLegendValue} />
        </PieChart>
      </ResponsiveContainer>
    </div>
  );
};

EditDistributionPieChart.propTypes = {
  data: PropTypes.array.isRequired,
};

const AccountingDocumentsPerfectChart = () => {
  const [{ data, loading }] = useAxios(
    `${process.env.REACT_APP_API_ENDPOINT}/statistic/accounting-document-perfect-edit-stats`
  );
  const [selectedOption, setSelectedOption] = useState('percentage_perfect');

  const handleDownload = () => {
    if (data) {
      const csv = stringify(data, { header: true });
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      saveAs(blob, 'accounting-document-perfect-edit-stats.csv');
    }
  };

  const cardTitle = (
    <CardTitle>
      <h2>{t('documents')}</h2>
    </CardTitle>
  );

  // Chart data keys to display
  const chartKeys = [
    'percentage_perfect',
    'accountingtext_perfect_pct',
    'company_perfect_pct',
    'transactionaccount_perfect_pct',
    'transactionvat_perfect_pct',
    'bankingtransaction_perfect_pct',
    'totalamount_perfect_pct',
    'currency_perfect_pct',
    'date_perfect_pct',
    'type_perfect_pct',
  ];

  const options = chartKeys.map((key) => ({
    value: key,
    label: t(key),
  }));

  const handleSelectChange = (e) => {
    setSelectedOption(e.target.value);
  };

  if (loading) {
    return (
      <Card className="mb-3">
        <CardBody>
          {cardTitle}
          <Spinner />
        </CardBody>
      </Card>
    );
  }

  return (
    <Card className="mb-3">
      <CardBody>
        {cardTitle}
        <FormGroup>
          <Input type="select" name="chartSelect" id="chartSelect" value={selectedOption} onChange={handleSelectChange}>
            {options.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </Input>
        </FormGroup>

        <AccountingDocumentPercentageChart data={data} dataKey={selectedOption} color={BAR_COLOR} />
        <div className="text-right">
          <Button color="primary" onClick={handleDownload}>
            {t('download_csv')}
          </Button>
        </div>
      </CardBody>
    </Card>
  );
};

const BankingDocumentsAverageEditsChart = () => {
  const [{ data, loading }] = useAxios(`${process.env.REACT_APP_API_ENDPOINT}/statistic/banking-document-edit-stats`);
  const [selectedOption, setSelectedOption] = useState('avg_total_edits');

  const handleDownload = () => {
    if (data) {
      const csv = stringify(data, { header: true });
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      saveAs(blob, 'banking-document-edit-stats.csv');
    }
  };

  const cardTitle = (
    <CardTitle>
      <h2>{t('banking_documents')}</h2>
    </CardTitle>
  );

  // Chart data keys to display
  const chartKeys = [
    'avg_total_edits',
    'avg_transaction_date_edits',
    'avg_transaction_description_edits',
    'avg_transaction_amount_edits',
  ];

  const options = chartKeys.map((key) => ({
    value: key,
    label: t(key),
  }));

  const handleSelectChange = (e) => {
    setSelectedOption(e.target.value);
  };

  if (loading) {
    return (
      <Card className="mb-3">
        <CardBody>
          {cardTitle}
          <Spinner />
        </CardBody>
      </Card>
    );
  }

  return (
    <Card className="mb-3">
      <CardBody>
        {cardTitle}
        <FormGroup>
          <Input
            type="select"
            name="bankingChartSelect"
            id="bankingChartSelect"
            value={selectedOption}
            onChange={handleSelectChange}
          >
            {options.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </Input>
        </FormGroup>

        <AverageEditsChart data={data} dataKey={selectedOption} color={BAR_COLOR} />

        <div className="text-right">
          <Button color="primary" onClick={handleDownload}>
            {t('download_csv')}
          </Button>
        </div>
      </CardBody>
    </Card>
  );
};

const AccountingDocumentsAverageEditsChart = () => {
  const [{ data, loading }] = useAxios(
    `${process.env.REACT_APP_API_ENDPOINT}/statistic/accounting-document-edit-stats`
  );
  const [selectedOption, setSelectedOption] = useState('avg_total_edits');

  const handleDownload = () => {
    if (data) {
      const csv = stringify(data, { header: true });
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      saveAs(blob, 'accounting-document-edit-stats.csv');
    }
  };

  const cardTitle = (
    <CardTitle>
      <h2>{t('documents')}</h2>
    </CardTitle>
  );

  const chartKeys = [
    'avg_total_edits',
    'avg_accounting_text_edits',
    'avg_company_edits',
    'avg_transaction_account_edits',
    'avg_transaction_vat_edits',
    'avg_banking_transaction_edits',
    'avg_total_amount_edits',
    'avg_currency_edits',
    'avg_date_edits',
    'avg_type_edits',
  ];

  const options = chartKeys.map((key) => ({
    value: key,
    label: t(key),
  }));

  const handleSelectChange = (e) => {
    setSelectedOption(e.target.value);
  };

  if (loading) {
    return (
      <Card className="mb-3">
        <CardBody>
          {cardTitle}
          <Spinner />
        </CardBody>
      </Card>
    );
  }

  return (
    <Card className="mb-3">
      <CardBody>
        {cardTitle}
        <FormGroup>
          <Input
            type="select"
            name="accountingEditsChartSelect"
            id="accountingEditsChartSelect"
            value={selectedOption}
            onChange={handleSelectChange}
          >
            {options.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </Input>
        </FormGroup>

        <AverageEditsChart data={data} dataKey={selectedOption} color={BAR_COLOR} />

        <div className="text-right">
          <Button color="primary" onClick={handleDownload}>
            {t('download_csv')}
          </Button>
        </div>
      </CardBody>
    </Card>
  );
};

// const EditTimeDocumentsChart = () => {
//   const [{ data, loading }] = useAxios(`${process.env.REACT_APP_API_ENDPOINT}/statistic/banking-document-edit-time`);

//   const cardTitle = (
//     <CardTitle>
//       <h2>{t('banking_document_edit_time')}</h2>
//     </CardTitle>
//   );

//   if (loading) {
//     return (
//       <Card className="mb-3">
//         <CardBody>
//           {cardTitle}
//           <Spinner />
//         </CardBody>
//       </Card>
//     );
//   }

//   return (
//     <Card className="mb-3">
//       <CardBody>
//         {cardTitle}
//         <EditTimeChart data={data} dataKey="avg_in_seconds" color={BAR_COLOR} />
//       </CardBody>
//     </Card>
//   );
// };

const EditDistributionChart = () => {
  const [{ data, loading }] = useAxios(
    `${process.env.REACT_APP_API_ENDPOINT}/statistic/accounting-document-edit-distribution`
  );

  const handleDownload = () => {
    if (data) {
      const csv = stringify(data, { header: true });
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      saveAs(blob, 'accounting-document-edit-distribution.csv');
    }
  };

  const cardTitle = (
    <CardTitle>
      <h2>{t('edit_distribution_last_30_days')}</h2>
    </CardTitle>
  );

  if (loading) {
    return (
      <Card className="mb-3">
        <CardBody>
          {cardTitle}
          <Spinner />
        </CardBody>
      </Card>
    );
  }

  return (
    <Card className="mb-3">
      <CardBody>
        {cardTitle}
        <EditDistributionPieChart data={data} />
        <div className="text-right">
          <Button color="primary" onClick={handleDownload}>
            {t('download_csv')}
          </Button>
        </div>
      </CardBody>
    </Card>
  );
};

const WorstCompaniesChart = () => {
  const [{ data, loading }] = useAxios(`${process.env.REACT_APP_API_ENDPOINT}/statistic/worst-companies`);

  const handleDownload = () => {
    if (data && data.length > 0 && data[0].data) {
      const companies = data[0].data;
      const csv = stringify(companies, { header: true });
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      saveAs(blob, 'worst-companies.csv');
    }
  };

  const cardTitle = (
    <CardTitle>
      <h2>{t('worst_companies_last_30_days')}</h2>
    </CardTitle>
  );

  if (loading) {
    return (
      <Card className="mb-3">
        <CardBody>
          {cardTitle}
          <Spinner />
        </CardBody>
      </Card>
    );
  }

  if (!data || data.length === 0 || !data[0].data) {
    return (
      <Card className="mb-3">
        <CardBody>
          {cardTitle}
          <div>No data available</div>
        </CardBody>
      </Card>
    );
  }

  const companies = data[0].data;

  return (
    <Card className="mb-3">
      <CardBody>
        {cardTitle}
        <div style={{ maxHeight: '600px', overflowY: 'auto' }}>
          <table className="table table-sm">
            <thead>
              <tr>
                <th>{t('correct_company')}</th>
                <th>{t('edit_count')}</th>
                <th>{t('commonly_detected_as')}</th>
              </tr>
            </thead>
            <tbody>
              {companies.map((company, index) => (
                <tr key={index}>
                  <td>{company.company}</td>
                  <td>{company.edit_count}</td>
                  <td style={{ color: '#333' }}>
                    {company.commonly_detected_as && company.commonly_detected_as.length > 0 ? (
                      <ul style={{ margin: 0, paddingLeft: '1rem' }}>
                        {company.commonly_detected_as.map((detection, detectionIndex) => (
                          <li key={detectionIndex}>
                            {detection.detected_company} ({detection.count})
                          </li>
                        ))}
                      </ul>
                    ) : (
                      'No detection errors'
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="text-right mt-3">
          <Button color="primary" onClick={handleDownload}>
            {t('download_csv')}
          </Button>
        </div>
      </CardBody>
    </Card>
  );
};

const WorstDossiersChart = () => {
  const [{ data, loading }] = useAxios(`${process.env.REACT_APP_API_ENDPOINT}/statistic/worst-dossiers`);

  const handleDownload = () => {
    if (data) {
      const csv = stringify(data, { header: true });
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      saveAs(blob, 'worst-dossiers.csv');
    }
  };

  const cardTitle = (
    <CardTitle>
      <h2>{t('worst_dossiers_last_30_days')}</h2>
    </CardTitle>
  );

  if (loading) {
    return (
      <Card className="mb-3">
        <CardBody>
          {cardTitle}
          <Spinner />
        </CardBody>
      </Card>
    );
  }

  if (!data || data.length === 0) {
    return (
      <Card className="mb-3">
        <CardBody>
          {cardTitle}
          <div>No data available</div>
        </CardBody>
      </Card>
    );
  }

  return (
    <Card className="mb-3">
      <CardBody>
        {cardTitle}
        <div style={{ maxHeight: '600px', overflowY: 'auto' }}>
          <table className="table table-sm">
            <thead>
              <tr>
                <th>{t('dossier_id')}</th>
                <th>{t('dossier_name')}</th>
                <th>{t('avg_manual_edits_per_document')}</th>
                <th>{t('total_documents_with_edits')}</th>
              </tr>
            </thead>
            <tbody>
              {data.map((dossier, index) => (
                <tr key={index}>
                  <td>{dossier.dossierId}</td>
                  <td>{dossier.dossier_name}</td>
                  <td>{dossier.avg_manual_edits_per_document}</td>
                  <td>{dossier.total_documents_with_edits}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="text-right mt-3">
          <Button color="primary" onClick={handleDownload}>
            {t('download_csv')}
          </Button>
        </div>
      </CardBody>
    </Card>
  );
};

export default function Charts() {
  return (
    <>
      <AccountingDocumentsAverageEditsChart />
      <AccountingDocumentsPerfectChart />
      <EditDistributionChart />
      <WorstCompaniesChart />
      <WorstDossiersChart />
      <BankingDocumentsAverageEditsChart />
    </>
  );
}
